import React, { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import { MonthlyReports } from "./MonthlyReports";
import { PoundSterling, ShoppingBag, RefreshCw, Users, Loader2, MapPin, TrendingUp, ArrowRight, TrendingDown } from "lucide-react";
import { api } from "../../api";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const COLORS = ["#1B4332", "#2D6A4F", "#40916C", "#52B788", "#74C69D", "#95D5B2", "#B7E4C7", "#a3b18a", "#588157", "#3a5a40"];

const Change = ({ pct, testId }) => {
  if (pct === null || pct === undefined) return <span className="text-xs text-[#8aa093]" data-testid={testId}>No data last month</span>;
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${up ? "bg-[#e3f4ea] text-[#1f7a45]" : "bg-[#fdecec] text-[#c1121f]"}`}>
      <Icon size={12} /> {up ? "+" : ""}{pct}%
    </span>
  );
};

const Stat = ({ icon: Icon, label, value, to, testId, pct }) => (
  <Link to={to} data-testid={testId} className="group block rounded-2xl border border-[#e6f0ea] bg-white p-5 transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:border-[#52B788] hover:shadow-md">
    <div className="mb-3 flex items-center justify-between">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#f0f7f2]"><Icon size={22} className="text-[#2D6A4F]" /></div>
      <ArrowRight size={18} className="text-[#9fb6a8] transition-transform group-hover:translate-x-1 group-hover:text-[#2D6A4F]" />
    </div>
    <p className="font-display text-3xl font-bold text-[#1B2a21]">{value}</p>
    <p className="mt-1 text-sm text-[#6b7d72]">{label}</p>
    <div className="mt-2"><Change pct={pct} testId={`${testId}-change`} /></div>
  </Link>
);

const ViewLink = ({ to, testId, children }) => (
  <Link to={to} data-testid={testId} className="ml-auto flex items-center gap-1 font-display text-xs font-semibold uppercase tracking-wide text-[#2D6A4F] hover:text-[#1B4332]">
    {children} <ArrowRight size={14} />
  </Link>
);

const Overview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading || !stats) {
    return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin text-[#2D6A4F]" size={32} /></div>;
  }

  const { monthly, top_places, all_places, product_sales, revenue_series } = stats;
  const maxOrders = Math.max(...all_places.map((p) => p.orders), 1);

  return (
    <div className="p-6 lg:p-8">
      <h1 className="font-display text-3xl font-bold uppercase text-[#1B2a21]">Overview</h1>
      <p className="mb-6 text-[#6b7d72]" data-testid="overview-month-label">
        <span className="font-semibold text-[#2D6A4F]">{monthly.month_label}</span> so far · % {monthly.compare_label}. Tiles restart on the 1st of each month.
      </p>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={PoundSterling} label="Revenue this month" pct={monthly.change.revenue} value={`£${monthly.current.revenue.toLocaleString()}`} to="/admin/orders" testId="overview-tile-revenue" />
        <Stat icon={ShoppingBag} label="Orders this month" pct={monthly.change.orders} value={monthly.current.orders} to="/admin/orders" testId="overview-tile-orders" />
        <Stat icon={RefreshCw} label="New subscriptions this month" pct={monthly.change.subscriptions} value={monthly.current.subscriptions} to="/admin/subscriptions" testId="overview-tile-subscriptions" />
        <Stat icon={Users} label="Customers this month" pct={monthly.change.customers} value={monthly.current.customers} to="/admin/email" testId="overview-tile-customers" />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* World map */}
        <div className="rounded-2xl border border-[#e6f0ea] bg-white p-5 lg:col-span-3">
          <div className="mb-2 flex items-center gap-2"><MapPin size={18} className="text-[#2D6A4F]" /><h2 className="font-display text-lg font-bold uppercase text-[#1B2a21]">Where We Sell (UK Areas)</h2><ViewLink to="/admin/orders" testId="overview-map-link">Orders</ViewLink></div>
          <div className="overflow-hidden rounded-xl bg-[#eaf4ee]">
            <ComposableMap projection="geoMercator" projectionConfig={{ center: [-3.0, 54.6], scale: 1900 }} height={360} style={{ width: "100%", height: "auto" }}>
              <Geographies geography={GEO_URL}>
                {({ geographies }) => geographies.map((geo) => (
                  <Geography key={geo.rsmKey} geography={geo} fill="#cfe3d6" stroke="#eaf4ee" strokeWidth={0.5}
                    style={{ default: { outline: "none" }, hover: { fill: "#B7E4C7", outline: "none" }, pressed: { outline: "none" } }} />
                ))}
              </Geographies>
              {all_places.filter((p) => p.coords).map((p) => (
                <Marker key={p.country} coordinates={p.coords}>
                  <circle r={Math.max(4, Math.min(14, 3 + p.orders))} fill="#2D6A4F" fillOpacity={0.5} stroke="#1B4332" strokeWidth={1} />
                  <text textAnchor="middle" y={-10} style={{ fontFamily: "Oswald, sans-serif", fontSize: 9, fontWeight: 600, fill: "#1B4332", textTransform: "uppercase" }}>{p.country}</text>
                </Marker>
              ))}
            </ComposableMap>
          </div>
        </div>

        {/* Top 6 places */}
        <div className="rounded-2xl border border-[#e6f0ea] bg-white p-5 lg:col-span-2">
          <div className="mb-4 flex items-center"><h2 className="font-display text-lg font-bold uppercase text-[#1B2a21]">Top 6 Places</h2><ViewLink to="/admin/orders" testId="overview-places-link">Orders</ViewLink></div>
          <div className="space-y-3">
            {top_places.map((p, i) => (
              <div key={p.country}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-semibold text-[#1B2a21]">{i + 1}. {p.country}</span>
                  <span className="text-[#6b7d72]">{p.orders} orders · £{p.revenue.toLocaleString()}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-[#f0f7f2]">
                  <div className="h-full rounded-full bg-[#2D6A4F]" style={{ width: `${(p.orders / maxOrders) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        {/* Pie chart */}
        <div className="rounded-2xl border border-[#e6f0ea] bg-white p-5 lg:col-span-2">
          <div className="mb-2 flex items-center"><h2 className="font-display text-lg font-bold uppercase text-[#1B2a21]">Product Sales %</h2><ViewLink to="/admin/products" testId="overview-products-link">Products</ViewLink></div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={product_sales.slice(0, 8)} dataKey="percent" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
                {product_sales.slice(0, 8).map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v}%`, n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1">
            {product_sales.slice(0, 6).map((p, i) => (
              <div key={p.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-[#34463c]"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{p.name}</span>
                <span className="font-semibold text-[#1B4332]">{p.percent}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue series */}
        <div className="rounded-2xl border border-[#e6f0ea] bg-white p-5 lg:col-span-3">
          <div className="mb-2 flex items-center gap-2"><TrendingUp size={18} className="text-[#2D6A4F]" /><h2 className="font-display text-lg font-bold uppercase text-[#1B2a21]">Revenue (last 14 days)</h2><ViewLink to="/admin/orders" testId="overview-revenue-link">Sales</ViewLink></div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef5f0" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#6b7d72" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6b7d72" }} />
              <Tooltip formatter={(v) => [`£${v}`, "Revenue"]} />
              <Bar dataKey="revenue" fill="#2D6A4F" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <MonthlyReports />
    </div>
  );
};

export default Overview;
